import React, { useContext } from 'react';
import styles from './../refunds.module.scss';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GenericTable from 'shared/components/GenericTable/genericTable';
import { format } from 'date-fns';
import { CONFIG_APP } from '../../../../shared/const/app.config';
import CurrencyComponent from '../../../../shared/components/Currency/Currency';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useModifyPolicy from '../../policyDetails/useModifyPolicy';
import SoftButton from '../../../../components/SoftButton';
import { UserDataContext } from 'shared/context/userDataContext';

const path = 'REFUNDS';

const StyledMenu = styled((props) => <Menu elevation={0} {...props} />)(() => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    minWidth: 180,
    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 0px 4px',
  },
}));

const RefundsTable = ({ data, tableUuid }) => {
  const { t } = useTranslation();
  const { modifyPolicy } = useModifyPolicy();
  const {
    businessUnits,
    selectedBusinessUnit,
    saveBusinessUnit,
    savePrevBusinessUnit,
    fetchShopId,
    selectedBrand,
    userRoles,
  } = useContext(UserDataContext);

  const navigate = useNavigate();

  const composeUrl = (policyId, ...routes) => {
    const searchParams = new URLSearchParams();

    if (routes && Array.isArray(routes) && routes.length !== 0) {
      return `/policy-details/${policyId}/${routes.join(
        '/',
      )}?${searchParams.toString()}`;
    }

    return `/policy-details/${policyId}?${searchParams.toString()}`;
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = ($event, rowValue) => {
    $event.stopPropagation();
    setAnchorEl($event.currentTarget);
    setSelectedRow(rowValue);
  };

  const handleClose = ($event, func = null) => {
    if ($event) $event.stopPropagation();

    if (func !== null && typeof func !== 'function') {
      throw new Error(
        `[Handle menu close] expected function, recived: ${typeof func} ${func}`,
      );
    }

    if (func !== null) {
      func(selectedRow);
    }

    setAnchorEl(null);
    setSelectedRow(null);
  };

  const ensurePolicyBrand = async (rowDetails) => {
    if (userRoles.viewAll && rowDetails.businessUnitId) {
      let addToList = false;
      let businessUnit =
        businessUnits.find(
          (businessUnit) => businessUnit.shopId === rowDetails.businessUnitId,
        ) || null;
      savePrevBusinessUnit(selectedBusinessUnit);

      if (!businessUnit) {
        const businessUnitResponse = await fetchShopId(
          rowDetails.businessUnitId,
        );
        if (!businessUnitResponse[0]) {
          throw new Error('[policies-tables] businessUnit id not found');
        }
        businessUnit = {
          ...businessUnitResponse[0],
          brand:
            businessUnitResponse[0]?.default_brand?.name ?? selectedBrand.brand,
        };
        addToList = true;
      }
      saveBusinessUnit(businessUnit, addToList);
    }
  };

  const goToViewPolicy = async (rowDetails, refundDetails) => {
    const { policyId, catalog } = rowDetails || {};

    if (!policyId) {
      throw new Error('[goToProfile] policyId is undefined');
    }

    await ensurePolicyBrand(rowDetails);

    const url = composeUrl(policyId);
    const urlWithRefundParam = `${url}&refund=${rowDetails.id}`;

    navigate(!refundDetails ? url : urlWithRefundParam, {
      state: { catalog, refundStatus: rowDetails.status },
    });
  };

  const getStatusStyles = (status) => {
    const statusStyles = {
      PENDING: styles?.warning,
      PAID: styles?.success,
      OVERDUE: styles?.danger,
      REISSUE: styles?.info,
    };
    return statusStyles[status] || '';
  };

  const columns = [
    {
      label: `${t(`${path}.businessUnitCode`)}`,
      value: (refund) => refund.businessUnitCode,
    },
    {
      label: `${t(`${path}.policyId`)}`,
      value: (refund) => refund.policyId,
    },
    {
      label: `${t(`${path}.policyStatus`)}`,
      value: (refund) => <strong>{refund.policyStatus}</strong>,
    },
    {
      label: `${t(`${path}.status`)}`,
      value: (refund) => {
        if (!refund.status) return <></>;

        return (
          <strong className={getStatusStyles(refund.status)}>
            {refund.status}
          </strong>
        );
      },
    },
    {
      label: `${t(`${path}.amount`)}`,
      value: (refund) => (
        <CurrencyComponent
          value={refund.amount.amount}
          countryCode={refund.amount.currency}
        ></CurrencyComponent>
      ),
    },
    {
      label: `${t(`${path}.requestDate`)}`,
      value: (refund) =>
        refund.requestDate
          ? format(new Date(refund.requestDate), CONFIG_APP.DATE_FORMAT)
          : null,
      width: '170px',
    },
    {
      label: `${t(`${path}.cancellationDate`)}`,
      value: (refund) =>
        refund.cancellationDate
          ? format(new Date(refund.cancellationDate), CONFIG_APP.DATE_FORMAT)
          : null,
      width: '140px',
    },
    {
      label: `${t(`${path}.refundDue`)}`,
      value: (refund) => {
        if (!refund?.refundDue && refund?.refundDue !== 0)
          return <strong>-</strong>;
        const isPositive = refund.refundDue > 0;
        return (
          <strong className={isPositive ? styles.success : styles.danger}>
            {`${refund.refundDue}d`}
          </strong>
        );
      },
      width: '150px',
      tooltip: `${t(`${path}.refundDueTooltip`)}`,
    },
    {
      label: `${t(`${path}.customerRefundDate`)}`,
      value: (refund) =>
        refund.customerRefundDate
          ? format(new Date(refund.customerRefundDate), CONFIG_APP.DATE_FORMAT)
          : null,
      width: '130px',
    },
    {
      label: t(`${path}.actions`),
      value: (rowValue) => (
        <>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={($event) => handleClick($event, rowValue)}
            sx={{
              '&:hover': { borderColor: '#0a76ec' },
            }}
          >
            Actions
            <KeyboardArrowDownIcon
              sx={{
                transform: 'translateX(50%)',
              }}
            />
          </SoftButton>
          <StyledMenu
            anchorEl={anchorEl}
            open={open}
            onClose={($event) => handleClose($event, null)}
          >
            <MenuItem
              onClick={($event) =>
                handleClose($event, (e) => goToViewPolicy(e, true))
              }
            >
              {t(`${path}.viewRefund`)}
            </MenuItem>
            <MenuItem onClick={($event) => handleClose($event, goToViewPolicy)}>
              {t(`${path}.viewPolicy`)}
            </MenuItem>
          </StyledMenu>
        </>
      ),
      width: '70px',
    },
  ];

  return (
    <div data-testid="RefundsTable">
      {!data || data.length === 0 ? (
        <p className="dataError">{t(`${path}.noRefunds`)}</p>
      ) : (
        <GenericTable
          tableUuid={tableUuid}
          data={data}
          columns={columns}
          path={path}
          onRowClick={(policy) => goToViewPolicy(policy, true)}
        />
      )}
    </div>
  );
};

RefundsTable.propTypes = {
  data: PropTypes.array.isRequired,
  tableUuid: PropTypes.string,
};

RefundsTable.defaultProps = {};

export default RefundsTable;
